import React from 'react';
import Container from '../layout/Container';
import Button from '../ui/Button';
import Eyebrow from '../ui/Eyebrow';
import EhrMock from '../ui/EhrMock';
import { colors, font } from '../../lib/tokens';

// Hero load sequence: each `.kc-enter` block rises a beat after the one
// before it; `delay` sets that beat via the --d custom property.
const delay = (ms: number) => ({ '--d': `${ms}ms` }) as React.CSSProperties;

export default function HeroSection() {
  return (
    <section
      id="hero"
      style={{
        background: colors.bg,
        paddingTop: 72,
        paddingBottom: 80,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Faint peak silhouette, filling the open space right of the headline.
          It traces itself in on load, then drifts gently against the scroll. */}
      <svg
        className="hidden md:block kc-draw-now kc-drift"
        width={620}
        height={500}
        viewBox="0 0 640 520"
        aria-hidden
        style={{
          position: 'absolute',
          right: -80,
          top: 24,
          opacity: 0.06,
          pointerEvents: 'none',
        }}
      >
        <path d="M40 480L320 80l280 400H40z" pathLength={1} fill="none" stroke={colors.ink} strokeWidth={1.5} />
        <path d="M160 480L320 260l160 220" pathLength={1} fill="none" stroke={colors.ink} strokeWidth={1} />
      </svg>

      <Container>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 960 }}>
          <div className="kc-enter" style={delay(0)}>
            <Eyebrow live>Live at Landspítali · Iceland’s national university hospital</Eyebrow>
          </div>
          <h1
            className="kc-enter kc-display text-balance"
            style={{
              ...delay(70),
              fontFamily: font.sans,
              fontWeight: 500,
              fontSize: 'clamp(42px, 6vw, 80px)',
              lineHeight: 0.98,
              letterSpacing: '-0.036em',
              color: colors.ink,
              margin: '24px 0 0',
            }}
          >
            Clinical coding,
            {' '}<br />
            <span className="kc-em">inferred</span> straight
            {' '}<br />
            from the note.
          </h1>
          <p
            className="kc-enter"
            style={{
              ...delay(140),
              fontSize: 20,
              lineHeight: 1.5,
              color: colors.ink2,
              maxWidth: 620,
              margin: '28px 0 0',
            }}
          >
            KatlaCode sits inside the hospital EHR, reads physician notes in real time, and proposes
            ICD-10 codes ranked by confidence and confirmed in one click.
          </p>
          <div
            className="kc-enter"
            style={{ ...delay(210), display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}
          >
            <Button variant="primary" href="mailto:info@katlacode.ai">Contact us</Button>
            <Button href="#product">
              See how it works <span className="kc-btn-arrow" aria-hidden>→</span>
            </Button>
          </div>
        </div>

        {/* Animated EHR mock */}
        <div className="kc-enter" style={{ ...delay(320), marginTop: 52, position: 'relative', zIndex: 1 }}>
          <EhrMock />
        </div>

        {/* Trust strip */}
        <div
          className="kc-enter kc-hero-partners"
          style={{
            ...delay(440),
            display: 'flex',
            alignItems: 'center',
            gap: 28,
            flexWrap: 'wrap',
            paddingTop: 28,
            marginTop: 48,
            borderTop: `1px solid ${colors.hair}`,
            color: colors.muted,
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          <span className="kc-hero-partners-label" style={{ color: colors.dim }}>Partners</span>
          {['Landspítali', 'University of Iceland', 'Almannarómur', 'Technology Development Fund'].map(
            (p, i) => (
              <React.Fragment key={p}>
                {i > 0 && (
                  <span className="kc-hero-partners-sep" style={{ opacity: 0.3 }}>/</span>
                )}
                <span>{p}</span>
              </React.Fragment>
            )
          )}
        </div>
      </Container>
    </section>
  );
}
